import './CanonicalNpcsDrawer.css';
import type {
  CanonicalNpcManifestEntry,
  CanonicalNpcCategory,
} from '../../services/host/CanonicalNpcsServiceModule';
import type {
  CanonicalNpcsLibraryState,
  CanonicalNpcsLibraryTab,
} from '../../hooks/useCanonicalNpcsLibrary';

/**
 * CanonicalNpcsDrawer — 2026-06-08 F3-FIX for Core Rulebook Chapter 18.
 *
 * 3-tab layout: Allies / Enemies / See Chapter 1 (the 4 NPCs whose stats
 * live in Chapter 1 and are stubbed here as cross-references — Felix Leiter,
 * Mary Goodnight, Holly Goodhead, Lt. Chong Sun Hip).
 *
 * Top-of-drawer banner surfaces the authoring-progress gap: 6 of ~28 NPCs
 * authored in the first batch; the rest enumerated in the seed's
 * `pending_remaining_npcs` field. This honesty is the anti-enshittification
 * lesson — the prior F3 commit framed an unrelated wire-up as "F3 done",
 * which silently hid the actual gap.
 */

const TAB_DEFS: ReadonlyArray<{ key: CanonicalNpcsLibraryTab; label: string }> = [
  { key: 'allies', label: 'Allies' },
  { key: 'enemies', label: 'Enemies' },
  { key: 'see_chapter_1', label: 'See Ch 1' },
];

const TAB_TO_CATEGORY: Record<CanonicalNpcsLibraryTab, CanonicalNpcCategory> = {
  allies: 'allies',
  enemies: 'enemies',
  see_chapter_1: 'see_chapter_1',
};

const STAT_ORDER: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'STR', label: 'STR' },
  { key: 'DEX', label: 'DEX' },
  { key: 'WIL', label: 'WIL' },
  { key: 'PER', label: 'PER' },
  { key: 'INT', label: 'INT' },
];

const PHYSICAL_ORDER: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'height', label: 'Height' },
  { key: 'weight', label: 'Weight' },
  { key: 'age', label: 'Age' },
  { key: 'appearance', label: 'Appearance' },
  { key: 'hand_to_hand_damage_class', label: 'H2H DC' },
  { key: 'stamina', label: 'Stamina' },
  { key: 'running_swimming', label: 'Run/Swim' },
  { key: 'carrying', label: 'Carrying' },
  { key: 'speed', label: 'Speed' },
];

export interface CanonicalNpcsDrawerProps {
  npcs: CanonicalNpcManifestEntry[];
  pendingRemainingCount: number;
  state: CanonicalNpcsLibraryState;
  error?: string | null;
  activeTab: CanonicalNpcsLibraryTab;
  onTabChange: (tab: CanonicalNpcsLibraryTab) => void;
  selectedNpcId: string | null;
  selectedNpcData: Record<string, unknown> | null;
  selectedNpcState: CanonicalNpcsLibraryState;
  onSelectNpc: (id: string) => void;
  onClose?: () => void;
  testidPrefix?: string;
}

function NpcDetail({
  data,
  testidPrefix,
}: {
  data: Record<string, unknown>;
  testidPrefix: string;
}): JSX.Element {
  const name = data['name'] as string | undefined;
  const manifestSection = data['manifest_section'] as string | undefined;
  const sourceMdLine = data['source_md_line'] as number | undefined;
  const stats = (data['stats'] ?? {}) as Record<string, number>;
  const skills = (data['skills'] ?? {}) as Record<
    string,
    { skill_level: number; primary_chance: number }
  >;
  const abilities = (data['abilities'] ?? []) as string[];
  const physical = (data['physical'] ?? {}) as Record<string, unknown>;
  const fields = (data['fields_of_experience'] ?? []) as string[];
  const weaknesses = (data['weaknesses'] ?? []) as string[];
  const famePoints = data['fame_points'] as number | undefined;
  const heroPoints = data['hero_points'] as number | undefined;
  const survivalPoints = data['survival_points'] as number | undefined;
  const background = (data['background'] as string) ?? '';
  const gameInformation = (data['game_information'] as string | null) ?? null;
  const bookKnownImplausibility = data['book_known_implausibility'] as
    | {field: string; book_value: unknown; note: string}
    | undefined;

  return (
    <div
      className="br-canonical-npc-detail"
      data-testid={`${testidPrefix}-detail`}
    >
      <div className="br-canonical-npc-detail-header">
        <div className="br-canonical-npc-detail-name br-display">{name}</div>
        <div className="br-canonical-npc-detail-citation br-label">
          Core Rulebook §{manifestSection}
          {sourceMdLine ? ` · MD L${sourceMdLine}` : ''}
        </div>
      </div>

      {bookKnownImplausibility && (
        <div
          className="br-canonical-npc-detail-warn"
          data-testid={`${testidPrefix}-detail-implausibility`}
        >
          <span className="br-canonical-npc-detail-warn-icon">⚠</span>
          <span className="br-canonical-npc-detail-warn-text">
            Book-known implausibility — {bookKnownImplausibility.field}={String(
              bookKnownImplausibility.book_value,
            )}: {bookKnownImplausibility.note}
          </span>
        </div>
      )}

      <div className="br-canonical-npc-detail-section">
        <div className="br-canonical-npc-detail-section-label">Stats</div>
        <dl className="br-canonical-npc-detail-stats">
          {STAT_ORDER.map((s) =>
            s.key in stats ? (
              <div key={s.key} className="br-canonical-npc-detail-stat-tile">
                <dt>{s.label}</dt>
                <dd className="br-mono">{stats[s.key]}</dd>
              </div>
            ) : null,
          )}
        </dl>
      </div>

      {Object.keys(skills).length > 0 && (
        <div className="br-canonical-npc-detail-section">
          <div className="br-canonical-npc-detail-section-label">Skills</div>
          <table className="br-canonical-npc-detail-skills">
            <thead>
              <tr>
                <th>Skill</th>
                <th>SL</th>
                <th>PC</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(skills).map(([k, v]) => (
                <tr key={k}>
                  <td>{k}</td>
                  <td className="br-mono">{v.skill_level}</td>
                  <td className="br-mono">{v.primary_chance}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {abilities.length > 0 && (
        <div className="br-canonical-npc-detail-section">
          <div className="br-canonical-npc-detail-section-label">Abilities</div>
          <ul className="br-canonical-npc-detail-chips">
            {abilities.map((a) => (
              <li key={a} className="br-canonical-npc-detail-chip">
                {a}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="br-canonical-npc-detail-section">
        <div className="br-canonical-npc-detail-section-label">Physical</div>
        <dl className="br-canonical-npc-detail-physical">
          {PHYSICAL_ORDER.map((p) =>
            physical[p.key] !== undefined && physical[p.key] !== null ? (
              <div key={p.key} className="br-canonical-npc-detail-physical-row">
                <dt>{p.label}</dt>
                <dd>{String(physical[p.key])}</dd>
              </div>
            ) : null,
          )}
        </dl>
      </div>

      <div className="br-canonical-npc-detail-section br-canonical-npc-detail-points">
        {famePoints !== undefined && (
          <div className="br-canonical-npc-detail-point-tile">
            <span className="br-canonical-npc-detail-point-label">Fame</span>
            <span className="br-canonical-npc-detail-point-value br-label">
              {famePoints}
            </span>
          </div>
        )}
        {heroPoints !== undefined && (
          <div className="br-canonical-npc-detail-point-tile">
            <span className="br-canonical-npc-detail-point-label">Hero</span>
            <span className="br-canonical-npc-detail-point-value br-label">
              {heroPoints}
            </span>
          </div>
        )}
        {survivalPoints !== undefined && (
          <div className="br-canonical-npc-detail-point-tile">
            <span className="br-canonical-npc-detail-point-label">Survival</span>
            <span className="br-canonical-npc-detail-point-value br-label">
              {survivalPoints}
            </span>
          </div>
        )}
      </div>

      {fields.length > 0 && (
        <div className="br-canonical-npc-detail-section">
          <div className="br-canonical-npc-detail-section-label">
            Fields of Experience
          </div>
          <ul className="br-canonical-npc-detail-chips">
            {fields.map((f) => (
              <li key={f} className="br-canonical-npc-detail-chip">
                {f}
              </li>
            ))}
          </ul>
        </div>
      )}

      {weaknesses.length > 0 && (
        <div className="br-canonical-npc-detail-section">
          <div className="br-canonical-npc-detail-section-label">Weaknesses</div>
          <ul className="br-canonical-npc-detail-chips">
            {weaknesses.map((w) => (
              <li key={w} className="br-canonical-npc-detail-chip">
                {w}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="br-canonical-npc-detail-section">
        <div className="br-canonical-npc-detail-section-label">Background</div>
        <p className="br-canonical-npc-detail-prose">{background}</p>
      </div>

      {gameInformation && (
        <div className="br-canonical-npc-detail-section">
          <div className="br-canonical-npc-detail-section-label">
            Game Information
          </div>
          <p className="br-canonical-npc-detail-prose">{gameInformation}</p>
        </div>
      )}
    </div>
  );
}

export function CanonicalNpcsDrawer({
  npcs,
  pendingRemainingCount,
  state,
  error,
  activeTab,
  onTabChange,
  selectedNpcId,
  selectedNpcData,
  selectedNpcState,
  onSelectNpc,
  onClose,
  testidPrefix = 'br-canonical-npcs-library',
}: CanonicalNpcsDrawerProps): JSX.Element {
  const visibleNpcs = npcs.filter(
    (n) => n.category === TAB_TO_CATEGORY[activeTab],
  );

  return (
    <aside
      className="br-scenario-drawer br-canonical-npcs-library-drawer"
      data-testid={testidPrefix}
      data-state={state}
    >
      <header>
        <h2 className="br-display">Canonical NPCs</h2>
        {/* 2026-06-09 Bug 1 drift fix — mirrors the 7 sibling drawers that
         *  already render-gate this on `onClose !== undefined`. Pre-fix
         *  the prop was optional but the close button rendered
         *  unconditionally, so a future caller omitting onClose would have
         *  a clickable-looking button that silently no-ops (Bug 1 shape).
         *  Today the sole caller (ScenarioRunner) always passes onClose,
         *  so this is type-safety + consistency hardening, not a
         *  runtime-observable bug at HEAD. */}
        {onClose !== undefined && (
          <button
            type="button"
            className="br-scenario-drawer-close"
            data-testid={`${testidPrefix}-close`}
            onClick={onClose}
            aria-label="Close Canonical NPCs drawer"
          >
            ✕
          </button>
        )}
      </header>

      <p
        className="br-label br-canonical-npc-banner"
        data-testid={`${testidPrefix}-banner`}
      >
        Core Rulebook Ch 18 — Allies and Enemies of James Bond + 4 see-Ch1
        cross-references. <strong>{npcs.length} authored</strong>
        {pendingRemainingCount > 0 ? (
          <>
            {' '}· {pendingRemainingCount} more enumerated as pending in the
            seed file.
          </>
        ) : (
          <> · 0 pending (F3-CONT closed the authoring gap).</>
        )}
      </p>

      <div className="br-scenario-drawer-tabs">
        {TAB_DEFS.map((t) => (
          <button
            key={t.key}
            type="button"
            data-testid={`${testidPrefix}-tab-${t.key}`}
            data-active={t.key === activeTab ? 'true' : 'false'}
            onClick={() => onTabChange(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {state === 'loading' && (
        <div className="br-label" data-testid={`${testidPrefix}-loading`}>
          Loading canonical NPCs…
        </div>
      )}
      {state === 'error' && (
        <div className="br-label" data-testid={`${testidPrefix}-error`}>
          {error ?? 'Failed to load canonical NPCs.'}
        </div>
      )}
      {state === 'ready' && (
        <>
          <div data-testid={`${testidPrefix}-count`} className="br-label">
            {visibleNpcs.length}
          </div>
          <ul
            className="br-canonical-npc-list"
            data-testid={`${testidPrefix}-list`}
          >
            {visibleNpcs.length === 0 && (
              <li className="br-label br-canonical-npc-empty">
                No NPCs in this category.
              </li>
            )}
            {visibleNpcs.map((n) => (
              <li key={n.id} className="br-canonical-npc-row-wrapper">
                <button
                  type="button"
                  className="br-canonical-npc-row"
                  data-testid={`${testidPrefix}-row-${n.id}`}
                  data-selected={n.id === selectedNpcId ? 'true' : 'false'}
                  onClick={() => onSelectNpc(n.id)}
                >
                  <span className="br-canonical-npc-row-name">{n.name}</span>
                  <span className="br-canonical-npc-row-section br-label">
                    §{n.manifest_section}
                  </span>
                </button>
                {n.id === selectedNpcId &&
                  selectedNpcState === 'loading' && (
                    <div
                      className="br-label"
                      data-testid={`${testidPrefix}-detail-loading`}
                    >
                      Loading detail…
                    </div>
                  )}
                {n.id === selectedNpcId &&
                  selectedNpcState === 'ready' &&
                  selectedNpcData && (
                    <NpcDetail
                      data={selectedNpcData}
                      testidPrefix={testidPrefix}
                    />
                  )}
                {n.id === selectedNpcId &&
                  selectedNpcState === 'error' && (
                    <div
                      className="br-label"
                      data-testid={`${testidPrefix}-detail-error`}
                    >
                      Failed to load detail.
                    </div>
                  )}
              </li>
            ))}
          </ul>
        </>
      )}
    </aside>
  );
}
